import React, { forwardRef } from 'react';
import { ConversionStockService } from '../../utils/converisonstock';

/**
 * Composant RestoPrint - Ticket et Facture unique dédié au Restaurant POS
 */
const RestoPrint = forwardRef((props, ref) => {
    const {
        panier = [],
        venteInfo = {},
        company = {},
        format = 'A6'
    } = props;

    const safeItems = Array.isArray(panier) ? panier : [];

    // --- FORMATAGE DE QUANTITÉ LOGISTIQUE ---
    const formaterQuantiteImpression = (qteSaisie, item) => {
        const gros = Number(item.saisie_gros || 0);
        const detail = Number(item.saisie_detail || 0);
        
        const libGros = item.unite_gros || item.unite_libelle_snap || item.libelle_gros_final || item.unite_code || item.unit_code_gros || "CS";
        const libDetail = item.unite_detail || item.unite_snap || item.libelle_detail_final || item.unite_reference || item.unit_ref_detail || "BTS";

        if (gros > 0 && detail > 0) {
            return `${gros} ${libGros} + ${detail} ${libDetail}`;
        } else if (gros > 0) {
            return `${gros} ${libGros}`;
        } else if (detail > 0) {
            return `${detail} ${libDetail}`;
        }

        try {
            const coeff = Number(item.article_complet?.coefficient || item.coefficient || item.ratio_conversion || 1);
            const totalPiecesNatives = Math.round(Number(qteSaisie || 0) * (coeff > 1 ? coeff : 1));
            const itemNormalise = { coefficient: coeff, code: libGros, unite_reference: libDetail };
            const resultatExpressive = ConversionStockService.toExpressionTextuelle(totalPiecesNatives, itemNormalise);
            
            if (resultatExpressive && !resultatExpressive.includes(libGros) && coeff > 1) {
                const cleanText = resultatExpressive.trim().startsWith('+') ? resultatExpressive.trim().substring(1).trim() : resultatExpressive;
                return `0 ${libGros} + ${cleanText}`;
            }
            return resultatExpressive || `${qteSaisie} ${libDetail}`;
        } catch (error) {
            return `${qteSaisie} ${libDetail}`;
        }
    };

    // --- REGROUPEMENT INTELLIGENT DES LIGNES ---
    const itemsAffiches = safeItems.reduce((acc, item) => {
        if (item.is_menu_child) return acc; // Masquer les composants internes

        let cleRegroupement = '';
        let nomAffiche = item.nom_article_snap || item.designation || 'ARTICLE';

        if (item.is_menu_parent) {
            const parts = nomAffiche.split('-');
            if (parts.length > 0) {
                cleRegroupement = parts[0].trim().toUpperCase();
                nomAffiche = cleRegroupement;
            } else {
                cleRegroupement = nomAffiche;
            }
        } else {
            cleRegroupement = `LIBRE-${item.id || item.product_id || Math.random()}`;
        }

        const existingIndex = acc.findIndex(i => i.cleGroup === cleRegroupement);

        if (existingIndex !== -1 && item.is_menu_parent) {
            // Cumul pour les menus de même nom global
            acc[existingIndex].montant_ttc_ligne = Number(acc[existingIndex].montant_ttc_ligne || 0) + Number(item.montant_ttc_ligne || 0);
            acc[existingIndex].montant_ht = Number(acc[existingIndex].montant_ht || 0) + Number(item.montant_ht || 0);
            acc[existingIndex].quantite = 1;
            // On met à jour le prix unitaire pour refléter le total cumulé si regroupé
            acc[existingIndex].prix_vente_unitaire = acc[existingIndex].montant_ttc_ligne;
        } else {
            acc.push({
                ...item,
                cleGroup: cleRegroupement,
                nom_article_snap: nomAffiche,
                quantite: item.is_menu_parent ? 1 : item.quantite,
                prix_vente_unitaire: item.is_menu_parent ? Number(item.montant_ttc_ligne || 0) : Number(item.prix_vente_unitaire || 0)
            });
        }

        return acc;
    }, []);

    const sousTotalHTNet = itemsAffiches.reduce((acc, cur) => {
        return acc + Number(cur.montant_ttc_ligne || cur.total_ttc || cur.montant_ht || 0);
    }, 0);
    const totalGeneral = sousTotalHTNet;

    const getLogoSrc = () => {
        const data = company.logo_data || company.logo || company.logo_url;
        if (!data) return null;
        if (data.startsWith('data:') || data.startsWith('http') || data.startsWith('blob:')) return data;
        return `data:image/png;base64,${data}`;
    };
    const logoSrc = getLogoSrc();

    return (
        <>
            <style>
                {`
                    @media print {
                        @page {
                            size: ${format === 'A5' ? 'A5 portrait' : 'A6 portrait'} !important;
                            margin: 0 !important;
                        }
                        html, body {
                            margin: 0 !important;
                            padding: 0 !important;
                            width: 100% !important;
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }
                        .restaurant-ticket-page {
                            width: ${format === 'A5' ? '148mm' : '105mm'} !important;
                            min-height: ${format === 'A5' ? '210mm' : '148mm'} !important;
                            box-sizing: border-box !important;
                            padding: 5mm !important;
                            margin: 0 auto !important;
                            background: #fff !important;
                        }
                    }
                `}
            </style>
            <div ref={ref} className="restaurant-ticket-page" style={s.page}>
                {/* ENTÊTE DU TICKET */}
                <div style={s.headerCenter}>
                    {logoSrc && <img src={logoSrc} alt="Logo" style={s.logoStyle} />}
                    <h2 style={s.companyName}>{company.name || company.nom || "LEDI RESTAURANT"}</h2>
                    <p style={s.textMeta}>{company.address || company.adresse || ""}</p>
                    <p style={s.textMeta}>Tél: {company.phone || company.telephone || "N/A"}</p>
                    
                    <div style={s.badgeTicket}>
                        {venteInfo.est_definitive ? "TICKET DE CAISSE" : "ADDITION / PROVISOIRE"}
                    </div>
                </div>

                {/* INFOS DE VENTE */}
                <div style={s.infoBox}>
                    <div style={s.infoRow}><span>N° Ticket :</span> <strong>{venteInfo.reference || venteInfo.lot_id || '---'}</strong></div>
                    <div style={s.infoRow}><span>Date :</span> <span>{new Date().toLocaleString('fr-FR')}</span></div>
                    <div style={s.infoRow}><span>Client :</span> <strong>{venteInfo.client_nom || "CLIENT AU COMPTANT"}</strong></div>
                    {venteInfo.table_name_snap && (
                        <div style={s.infoRow}><span>Table :</span> <strong>{venteInfo.table_name_snap}</strong></div>
                    )}
                    {venteInfo.vendeur && (
                        <div style={s.infoRow}><span>Caissier/Serveur :</span> <span>{venteInfo.vendeur}</span></div>
                    )}
                </div>

                {/* TABLE DES ARTICLES / MENUS REGROUPÉS */}
                <table style={s.table}>
                    <thead>
                        <tr>
                            <th style={{ ...s.th, textAlign: 'left', width: '40%' }}>DÉSIGNATION</th>
                            <th style={{ ...s.th, textAlign: 'center', width: '15%' }}>QTÉ</th>
                            <th style={{ ...s.th, textAlign: 'right', width: '22%' }}>P.U</th>
                            <th style={{ ...s.th, textAlign: 'right', width: '23%' }}>TOTAL</th>
                        </tr>
                    </thead>
                    <tbody>
                        {itemsAffiches.map((item, index) => {
                            const designation = item.nom_article_snap || item.designation || item.nom || 'Article';
                            const qteAffichee = item.is_menu_parent ? 1 : formaterQuantiteImpression(item.quantite, item);
                            const pu = Number(item.prix_vente_unitaire || 0);
                            const totalLigne = Number(item.montant_ttc_ligne || 0);

                            return (
                                <tr key={item.id || index} style={s.tr}>
                                    <td style={{ ...s.td, textAlign: 'left', fontWeight: item.is_menu_parent ? 'bold' : 'normal' }}>
                                        {designation}
                                    </td>
                                    <td style={{ ...s.td, textAlign: 'center' }}>{qteAffichee}</td>
                                    <td style={{ ...s.td, textAlign: 'right', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{pu.toLocaleString()}</td>
                                    <td style={{ ...s.td, textAlign: 'right', fontWeight: 'bold', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{totalLigne.toLocaleString()} F</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                {/* TOTAUX */}
                <div style={s.totalSection}>
                    <div style={s.totalRowFinal}>
                        <span>TOTAL À PAYER :</span>
                        <span>{totalGeneral.toLocaleString()} FCFA</span>
                    </div>
                    {venteInfo.est_definitive && (
                        <>
                            <div style={s.totalRowSub}>
                                <span>Montant Reçu :</span>
                                <span>{Number(venteInfo.montant_recu || 0).toLocaleString()} F</span>
                            </div>
                            <div style={s.totalRowSub}>
                                <span>Monnaie Rendue :</span>
                                <span>{Number(venteInfo.reliquat || 0).toLocaleString()} F</span>
                            </div>
                        </>
                    )}
                </div>

                {/* PIED DE PAGE */}
                <div style={s.footer}>
                    <p style={{ margin: '2px 0', fontWeight: 'bold' }}>Merci de votre visite et bon appétit !</p>
                    <p style={{ fontSize: '8px', color: '#666', margin: '2px 0' }}>ERP LEDI EXPERT PRO - Gestion Restaurant</p>
                </div>
            </div>
        </>
    );
});

const s = {
    page: {
        fontFamily: '"Courier New", Courier, monospace',
        fontSize: '10px',
        color: '#000',
        background: '#fff',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box'
    },
    headerCenter: {
        textAlign: 'center',
        marginBottom: '8px',
        borderBottom: '1px dashed #000',
        paddingBottom: '6px'
    },
    logoStyle: {
        maxHeight: '12mm',
        objectFit: 'contain',
        marginBottom: '4px'
    },
    companyName: {
        fontSize: '13px',
        fontWeight: 'bold',
        margin: '2px 0',
        textTransform: 'uppercase'
    },
    textMeta: {
        fontSize: '9px',
        margin: '1px 0'
    },
    badgeTicket: {
        display: 'inline-block',
        marginTop: '4px',
        padding: '2px 8px',
        fontSize: '9px',
        fontWeight: 'bold',
        border: '1px solid #000',
        borderRadius: '3px'
    },
    infoBox: {
        fontSize: '9px',
        marginBottom: '8px',
        borderBottom: '1px dashed #000',
        paddingBottom: '6px'
    },
    infoRow: {
        display: 'flex',
        justifyContent: 'space-between',
        margin: '2px 0'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        marginBottom: '8px'
    },
    th: {
        borderBottom: '1px solid #000',
        padding: '4px 2px',
        fontSize: '9px',
        fontWeight: 'bold'
    },
    tr: {
        borderBottom: '1px dotted #ccc'
    },
    td: {
        padding: '5px 2px',
        fontSize: '9px',
        verticalAlign: 'top'
    },
    totalSection: {
        borderTop: '1px solid #000',
        borderBottom: '1px dashed #000',
        padding: '6px 0',
        marginBottom: '10px'
    },
    totalRowFinal: {
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '12px',
        fontWeight: 'bold',
        margin: '3px 0'
    },
    totalRowSub: {
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '9px',
        margin: '2px 0'
    },
    footer: {
        textAlign: 'center',
        marginTop: 'auto',
        fontSize: '9px',
        borderTop: '1px dashed #000',
        paddingTop: '6px'
    }
};

RestoPrint.displayName = 'RestoPrint';
export default RestoPrint;